import { useState, useRef } from 'react';

interface Props {
  src: string;
  alt: string;
  fallback?: string;
  className?: string;
  /** Layout mode: 'cover' forces object-cover, 'contain' forces object-contain, 'adaptive' auto-detects */
  mode?: 'cover' | 'contain' | 'adaptive' | 'natural';
  referrerPolicy?: string;
  onError?: (e: React.SyntheticEvent<HTMLImageElement>) => void;
  /** Reports the real image ratio so a parent card can match the image instead of cropping it. */
  onAspectRatio?: (ratio: number) => void;
}

/**
 * SmartImage - auto-detects aspect ratio on load and applies the right object-fit.
 * - Landscape (>= 1.2:1): object-cover (fills the container, crops edges)
 * - Square-ish (0.8–1.2): object-cover but positioned to show full image where possible
 * - Portrait (< 0.8): object-contain on a neutral bg (no aggressive top/bottom crop)
 * - Natural: reports the ratio but leaves the parent responsible for the frame geometry.
 */
export default function SmartImage({
  src,
  alt,
  fallback = 'https://images.unsplash.com/photo-1565793298595-6a879b1d9492?w=600&q=80',
  className = '',
  mode = 'adaptive',
  referrerPolicy = 'no-referrer',
  onError,
  onAspectRatio,
}: Props) {
  const [fitMode, setFitMode] = useState<'cover' | 'contain'>(
    mode === 'cover' ? 'cover' : mode === 'contain' ? 'contain' : 'cover'
  );
  const [hasError, setHasError] = useState(false);
  const fallbackUsed = useRef(false);

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const { naturalWidth, naturalHeight } = img;
    if (naturalWidth === 0 || naturalHeight === 0) return;
    const ratio = naturalWidth / naturalHeight;
    onAspectRatio?.(ratio);
    if (mode !== 'adaptive') return;
    // Portrait or square → contain to avoid bad crops
    if (ratio < 1.2) {
      setFitMode('contain');
    } else {
      setFitMode('cover');
    }
  };

  const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    if (!fallbackUsed.current) {
      fallbackUsed.current = true;
      setHasError(true);
      (e.target as HTMLImageElement).src = fallback;
    } else {
      onError?.(e);
    }
  };

  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      decoding="async"
      referrerPolicy={referrerPolicy as React.ImgHTMLAttributes<HTMLImageElement>['referrerPolicy']}
      onLoad={handleLoad}
      onError={handleError}
      className={`${className} ${mode === 'natural' || fitMode === 'contain' ? 'object-contain' : 'object-cover'}`}
    />
  );
}
